import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityTeamTag } from '@reg/shared/model';
import { HttpServiceHelper, IdentityCalendarItemService } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save team tags.
 */
@Injectable()
export class IdentityTeamTagService {
    private identityApiBaseUrl: string;
    private cachedMap: { [apiRequest: string]: IdentityTeamTag[]; } = {};
    private cachedTeamTag: { [apiRequest: string]: IdentityTeamTag; } = {};

    /**
     * Constructor
     */
    constructor(
                private http: HttpServiceHelper,
                private identityCalendarItemService: IdentityCalendarItemService,
    ) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    public clearCache(): void {
        this.cachedMap = {};
        this.cachedTeamTag = {};
        this.identityCalendarItemService.clearCache();
    }

    /**
     * Get team tags
     */
    public getIndex(
        identityTeamId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityTeamTag[]> {
        const apiRequestUrl: string = this.getApiUrl(identityTeamId, null, includes, queryParams);

        if (this.cachedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedMap[apiRequestUrl].map((a: IdentityTeamTag) => a.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const teamTags: IdentityTeamTag[] = [];

                const bodyData: any = response.data || [];
                for (const tag of bodyData) {
                    teamTags.push((new IdentityTeamTag()).deserialize(tag));
                }

                this.cachedMap[apiRequestUrl] = teamTags;
                return teamTags.map((i: IdentityTeamTag) => i.clone());
            }));
    }

    /**
     * Get a single teamTag
     */
    public get(
        identityTeamId: string,
        identityTeamTagId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityTeamTag> {
        const apiRequestUrl: string = this.getApiUrl(identityTeamId, identityTeamTagId, includes, queryParams);

        if (this.cachedTeamTag[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(this.cachedTeamTag[apiRequestUrl].clone());
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const teamTag: IdentityTeamTag = (new IdentityTeamTag().deserialize(response));
                this.cachedTeamTag[apiRequestUrl] = teamTag.clone();
                return teamTag;
            }));
    }

    /**
     * Add or update a teamTag
     */
    public save(
        teamTag: IdentityTeamTag,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityTeamTag> {
        const teamTagId: string = teamTag.isNew ? null : teamTag.id;
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(teamTag.teamId, teamTagId, includes, queryParams);

        if (!teamTag.isNew) {
            // Update existing TeamTag
            response = this.http.patch(apiRequestUrl, teamTag.serialize());
        } else {
            // Add a new TeamTag
            response = this.http.post(apiRequestUrl, teamTag.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityTeamTag()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified tag.
     */
    public delete(identityTeamId: string, identityTeamTagId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(identityTeamId, identityTeamTagId);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        identityTeamId: string,
        teamTagId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/teams/${identityTeamId}/tags/`;

        if (teamTagId) {
            url += teamTagId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

